// Procurement page — Phase 3 implementation.
//
// Auditor-grade procurement event log under 2 CFR 200.318–.327:
//   - Events tab: live table of every RFP/IFB/quote/sole-source event,
//     with threshold-tier-aware compliance flags (formal procurements
//     missing IGE / cost analysis / MBE solicitation get red flags).
//   - New-event drawer: inline form, threshold tier auto-classifies as
//     you type the estimated amount.
//   - Obligations tab: catalog-driven obligation rows for procurement
//     (FED-FIN-PROC, FED-FIN-DBE, FED-FIN-COI, FED-FIN-LOBBY).
//
// Backend:
//   GET  /api/bead-pm/projects/:id/procurement-events
//   POST /api/bead-pm/projects/:id/procurement-events
//   PUT  /api/bead-pm/projects/:id/procurement-events/:eventId
//   DELETE /api/bead-pm/projects/:id/procurement-events/:eventId
//   GET  /api/bead-pm/projects/:id/obligations?category=federal_financial
//   GET  /api/bead/vendors

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import DocumentUploader from '../components/DocumentUploader';

type Project = { id: string; project_name: string; project_code: string };
type Vendor = { id: string; name: string; uei: string | null };

type ProcurementEvent = {
  id: string;
  project_id: string;
  event_code: string;
  procurement_type: 'micro_purchase' | 'simplified' | 'sealed_bid' | 'rfp' | 'sole_source';
  threshold_tier: 'micro' | 'simplified' | 'formal';
  description: string;
  estimated_amount_usd: string | number;
  igce_amount_usd: string | number | null;
  igce_document_id: string | null;
  cost_analysis_document_id: string | null;
  conflict_disclosure_document_id: string | null;
  vendors_solicited: Array<{ name: string; mbe?: boolean; wbe?: boolean; veteran?: boolean }>;
  bids_received_count: number | null;
  awarded_vendor_id: string | null;
  awarded_vendor_name: string | null;
  award_amount_usd: string | number | null;
  award_date: string | null;
  contract_type: string | null;
  status: 'planned' | 'soliciting' | 'evaluating' | 'awarded' | 'cancelled';
  notes: string | null;
  created_at: string;
};

type Obligation = {
  id: string; code: string; name: string; cadence_rule: string;
  source_cite: string | null; due_date: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'accepted' | 'rejected' | 'overdue' | 'na';
};

const PROCUREMENT_OBLIGATION_CODES = new Set([
  'FED-FIN-PROC', 'FED-FIN-DBE', 'FED-FIN-COI', 'FED-FIN-LOBBY',
]);

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending:     { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  submitted:   { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  accepted:    { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  rejected:    { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue:     { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na:          { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
  planned:     { bg: '#f1f5f9', fg: '#475569', label: 'Planned' },
  soliciting:  { bg: '#dbeafe', fg: '#1e40af', label: 'Soliciting' },
  evaluating:  { bg: '#fef3c7', fg: '#92400e', label: 'Evaluating' },
  awarded:     { bg: '#d1fae5', fg: '#065f46', label: 'Awarded' },
  cancelled:   { bg: '#fee2e2', fg: '#991b1b', label: 'Cancelled' },
};

const TIER_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  micro:      { bg: '#d1fae5', fg: '#065f46', label: 'Micro' },
  simplified: { bg: '#dbeafe', fg: '#1e40af', label: 'Simplified' },
  formal:     { bg: '#fee2e2', fg: '#991b1b', label: 'Formal' },
};

const TYPE_LABEL: Record<string, string> = {
  micro_purchase: 'Micro-purchase',
  simplified:     'Simplified quote',
  sealed_bid:     'Sealed bid (IFB)',
  rfp:            'Request for proposals (RFP)',
  sole_source:    'Sole-source justification',
};

function deriveTier(amount: number): 'micro' | 'simplified' | 'formal' {
  if (amount < 15000) return 'micro';
  if (amount < 350000) return 'simplified';
  return 'formal';
}

function fmtUSD(n: number | string | null | undefined): string {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}k`;
  return `$${Math.round(v).toLocaleString()}`;
}

function Badge({ status, map }: { status: string; map: typeof STATUS_BADGE }) {
  const s = map[status] ?? STATUS_BADGE.pending;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '2px 8px',
      borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border, #e2e8f0)',
      borderTop: `3px solid ${color ?? '#3b82f6'}`,
      borderRadius: 6, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.06em', color: 'var(--text-muted, #64748b)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const TIER_RULES: Record<'micro' | 'simplified' | 'formal', { label: string; rule: string; required: string[] }> = {
  micro: {
    label: '< $15,000 — micro-purchase',
    rule: 'No competitive quotes required if price is reasonable.',
    required: ['Reasonableness basis (notes)'],
  },
  simplified: {
    label: '$15k–$350k — simplified acquisition',
    rule: '≥ 2 quotes from qualified sources.',
    required: ['Bids received ≥ 2', 'Selection rationale (notes)'],
  },
  formal: {
    label: '> $350k — formal procurement',
    rule: 'Sealed bids OR RFP with public notice.',
    required: ['IGE document', 'Cost analysis', 'MBE/WBE/veteran solicitation log', 'COI disclosure'],
  },
};

/**
 * For each event, return what's MISSING vs the tier requirements.
 * Empty array = compliant.
 */
function complianceGaps(e: ProcurementEvent): string[] {
  const gaps: string[] = [];
  if (e.threshold_tier === 'formal') {
    if (!e.igce_amount_usd && !e.igce_document_id) gaps.push('IGE');
    if (!e.cost_analysis_document_id)              gaps.push('Cost analysis');
    const hasDbe = (e.vendors_solicited ?? []).some(v => v.mbe || v.wbe || v.veteran);
    if (!hasDbe)                                    gaps.push('MBE/WBE solicitation');
    if (!e.conflict_disclosure_document_id)         gaps.push('COI disclosure');
  }
  if (e.threshold_tier === 'simplified') {
    if (!e.bids_received_count || e.bids_received_count < 2) gaps.push('< 2 bids');
  }
  return gaps;
}

export default function PurchaseOrdersPage() {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [tab, setTab] = useState<'events' | 'obligations'>('events');
  const [showNew, setShowNew] = useState(false);

  const { data: projects } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });
  const activeProjectId = projectId ?? projects?.[0]?.id ?? null;

  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: ['bead-vendors-list'],
    queryFn: () => api.get('/bead/vendors'),
  });

  const { data: eventsData } = useQuery<{ items: ProcurementEvent[] }>({
    queryKey: ['procurement-events', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/procurement-events`),
    enabled: !!activeProjectId,
  });
  const events: ProcurementEvent[] = eventsData?.items ?? [];

  const { data: financialObligations = [] } = useQuery<Obligation[]>({
    queryKey: ['fin-obligations', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/obligations?category=federal_financial`),
    enabled: !!activeProjectId,
  });

  const procurementObligations = useMemo(
    () => financialObligations.filter(o => PROCUREMENT_OBLIGATION_CODES.has(o.code)),
    [financialObligations]
  );

  const createEvent = useMutation({
    mutationFn: (body: any) => api.post(`/bead-pm/projects/${activeProjectId}/procurement-events`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement-events', activeProjectId] });
      setShowNew(false);
    },
  });

  const kpis = useMemo(() => {
    const total = events.length;
    const flagged = events.filter(e => complianceGaps(e).length > 0).length;
    const awarded = events.filter(e => e.status === 'awarded').length;
    const totalAwarded = events
      .filter(e => e.status === 'awarded')
      .reduce((acc, e) => acc + Number(e.award_amount_usd ?? 0), 0);
    return { total, flagged, awarded, totalAwarded };
  }, [events]);

  return (
    <div>
      <div className="page__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>Procurement</h1>
          <div className="page__subtitle" style={{ color: 'var(--text-muted, #64748b)', fontSize: 13 }}>
            RFPs, sealed bids, quotes, sole-source — full audit trail under 2 CFR 200.318–.327
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={activeProjectId ?? ''}
            onChange={(e) => setProjectId(e.target.value || null)}
            style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border, #e2e8f0)', borderRadius: 6 }}
          >
            {(projects ?? []).map(p => (
              <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>
            ))}
          </select>
          {tab === 'events' && (
            <button
              onClick={() => setShowNew(s => !s)}
              style={{
                padding: '6px 14px', background: '#0f172a', color: '#fff',
                border: '1px solid #0f172a', borderRadius: 4, fontSize: 12,
                fontWeight: 600, cursor: 'pointer',
              }}
            >{showNew ? '× Cancel' : '+ New event'}</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <StatCard label="Total events"      value={kpis.total} color="#3b82f6" />
        <StatCard label="Compliance flags"  value={kpis.flagged} sub={kpis.flagged > 0 ? 'See red badges below' : 'All clean'} color={kpis.flagged > 0 ? '#ef4444' : '#10b981'} />
        <StatCard label="Awarded"           value={kpis.awarded} color="#10b981" />
        <StatCard label="Total awarded"     value={fmtUSD(kpis.totalAwarded)} color="#0f172a" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border, #e2e8f0)', marginBottom: 16 }}>
        {[
          { id: 'events',      label: 'Events' },
          { id: 'obligations', label: 'Obligations' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            style={{
              padding: '8px 14px', border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid #0f172a' : '2px solid transparent',
              color: tab === t.id ? '#0f172a' : '#64748b',
              fontWeight: tab === t.id ? 600 : 500, fontSize: 13, cursor: 'pointer', marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'events' && (
        <>
          {showNew && activeProjectId && (
            <NewEventForm
              vendors={vendors}
              onCancel={() => setShowNew(false)}
              onSubmit={(body) => createEvent.mutate(body)}
              isSubmitting={createEvent.isPending}
            />
          )}

          {/* Events table */}
          <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
                <tr>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Code</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Type</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Tier</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Description</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Estimated</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Award</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Bids</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Status</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Compliance</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: 24, color: '#94a3b8', textAlign: 'center' }}>
                    No procurement events yet. Click <b>+ New event</b> to log one.
                  </td></tr>
                ) : events.map(e => {
                  const gaps = complianceGaps(e);
                  return (
                    <tr key={e.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#0f172a', fontWeight: 600 }}>{e.event_code}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12 }}>{TYPE_LABEL[e.procurement_type] ?? e.procurement_type}</td>
                      <td style={{ padding: '8px 10px' }}><Badge status={e.threshold_tier} map={TIER_BADGE} /></td>
                      <td style={{ padding: '8px 10px', fontSize: 12, maxWidth: 280 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.description}>{e.description}</div>
                        {e.awarded_vendor_name && (
                          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>→ {e.awarded_vendor_name}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(e.estimated_amount_usd)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(e.award_amount_usd)}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: '#64748b' }}>{e.bids_received_count ?? '—'}</td>
                      <td style={{ padding: '8px 10px' }}><Badge status={e.status} map={STATUS_BADGE} /></td>
                      <td style={{ padding: '8px 10px' }}>
                        {gaps.length === 0 ? (
                          <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>Clean</span>
                        ) : (
                          <span title={gaps.join(' · ')} style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                            ⚠ {gaps.length} missing
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Threshold rules card */}
          <h2 style={{ fontSize: 14, marginBottom: 8, color: '#64748b' }}>Threshold rules (2 CFR 200.320)</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {(['micro', 'simplified', 'formal'] as const).map(tier => {
              const r = TIER_RULES[tier];
              const tb = TIER_BADGE[tier];
              return (
                <div key={tier} style={{
                  background: '#fff', border: '1px solid var(--border, #e2e8f0)',
                  borderTop: `3px solid ${tb.fg}`,
                  borderRadius: 6, padding: '12px 14px',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: '#0f172a', marginTop: 6 }}>{r.rule}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Required artifacts:</div>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 11, color: '#64748b' }}>
                    {r.required.map(x => <li key={x}>{x}</li>)}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'obligations' && (
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Code</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Requirement</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Authority</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Cadence</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Due</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {procurementObligations.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#64748b' }}>{o.code}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{o.name}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.source_cite ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.cadence_rule.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.due_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><Badge status={o.status} map={STATUS_BADGE} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// New-event drawer
// -----------------------------------------------------------------------------

function NewEventForm({
  vendors, onCancel, onSubmit, isSubmitting,
}: {
  vendors: Vendor[];
  onCancel: () => void;
  onSubmit: (body: any) => void;
  isSubmitting: boolean;
}) {
  const [eventCode, setEventCode] = useState('');
  const [type, setType] = useState<'rfp' | 'sealed_bid' | 'simplified' | 'micro_purchase' | 'sole_source'>('rfp');
  const [description, setDescription] = useState('');
  const [estimated, setEstimated] = useState('');
  const [igce, setIgce] = useState('');
  const [bids, setBids] = useState('');
  const [status, setStatus] = useState<'planned' | 'soliciting' | 'evaluating' | 'awarded' | 'cancelled'>('planned');
  const [contractType, setContractType] = useState<string>('firm_fixed_price');
  const [awardedVendor, setAwardedVendor] = useState('');
  const [awardAmount, setAwardAmount] = useState('');
  const [awardDate, setAwardDate] = useState('');
  const [vendorsList, setVendorsList] = useState('');  // newline-separated
  const [includeMbe, setIncludeMbe] = useState(false);
  const [notes, setNotes] = useState('');
  const [igceDocId, setIgceDocId] = useState<string | null>(null);
  const [costAnalysisDocId, setCostAnalysisDocId] = useState<string | null>(null);
  const [coiDocId, setCoiDocId] = useState<string | null>(null);
  const [contractDocId, setContractDocId] = useState<string | null>(null);

  const estNum = Number(estimated) || 0;
  const tier = deriveTier(estNum);
  const tb = TIER_BADGE[tier];

  function handleSubmit() {
    const vlist = vendorsList.split('\n').map(s => s.trim()).filter(Boolean);
    const vobjs = vlist.map((name, i) => ({
      name,
      // Mark first vendor as MBE/WBE if checkbox is on (placeholder UX —
      // real flow will be checkboxes per row).
      ...(includeMbe && i === 0 ? { mbe: true } : {}),
    }));
    onSubmit({
      event_code: eventCode,
      procurement_type: type,
      description,
      estimated_amount_usd: estNum,
      igce_amount_usd: igce ? Number(igce) : null,
      bids_received_count: bids ? Number(bids) : null,
      awarded_vendor_id: awardedVendor || null,
      award_amount_usd: awardAmount ? Number(awardAmount) : null,
      award_date: awardDate || null,
      contract_type: contractType,
      status,
      vendors_solicited: vobjs,
      notes: notes || null,
      igce_document_id: igceDocId,
      cost_analysis_document_id: costAnalysisDocId,
      conflict_disclosure_document_id: coiDocId,
      contract_document_id: contractDocId,
    });
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '6px 10px', fontSize: 12,
    border: '1px solid var(--border, #e2e8f0)', borderRadius: 4,
    fontFamily: 'inherit', background: '#fff',
  };
  const lbl: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '.06em', color: '#64748b', marginBottom: 4, display: 'block',
  };

  return (
    <div style={{
      background: '#f8fafc', border: '1px solid var(--border, #e2e8f0)',
      borderRadius: 6, padding: 16, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>New procurement event</h3>
        <span style={{
          background: tb.bg, color: tb.fg, padding: '4px 12px', borderRadius: 999,
          fontSize: 11, fontWeight: 700,
        }}>{tb.label} tier — auto-classified</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Event code</label>
          <input style={inp} value={eventCode} onChange={e => setEventCode(e.target.value)} placeholder="RFP-2026-XX" />
        </div>
        <div>
          <label style={lbl}>Type</label>
          <select style={inp} value={type} onChange={e => setType(e.target.value as any)}>
            <option value="rfp">RFP</option>
            <option value="sealed_bid">Sealed bid (IFB)</option>
            <option value="simplified">Simplified quote</option>
            <option value="micro_purchase">Micro-purchase</option>
            <option value="sole_source">Sole-source</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Estimated amount ($)</label>
          <input style={inp} type="number" value={estimated} onChange={e => setEstimated(e.target.value)} placeholder="0" />
        </div>
        <div>
          <label style={lbl}>{'IGE ($) — required > $350k'}</label>
          <input style={inp} type="number" value={igce} onChange={e => setIgce(e.target.value)} placeholder="—" />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Description</label>
        <input style={inp} value={description} onChange={e => setDescription(e.target.value)} placeholder="What's being procured?" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Vendors solicited (one per line)</label>
          <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
            value={vendorsList} onChange={e => setVendorsList(e.target.value)}
            placeholder={'Acme Corp\nPrime Construction\nTribal Net LLC'} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 11, color: '#64748b' }}>
            <input type="checkbox" checked={includeMbe} onChange={e => setIncludeMbe(e.target.checked)} />
            First vendor is MBE / WBE / veteran-owned
          </label>
        </div>
        <div>
          <label style={lbl}>Notes (incl. cost-reasonableness basis)</label>
          <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Selection rationale, COI disclosed, etc." />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Bids received</label>
          <input style={inp} type="number" value={bids} onChange={e => setBids(e.target.value)} placeholder="—" />
        </div>
        <div>
          <label style={lbl}>Awarded vendor</label>
          <select style={inp} value={awardedVendor} onChange={e => setAwardedVendor(e.target.value)}>
            <option value="">— none —</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Award amount ($)</label>
          <input style={inp} type="number" value={awardAmount} onChange={e => setAwardAmount(e.target.value)} placeholder="—" />
        </div>
        <div>
          <label style={lbl}>Award date</label>
          <input style={inp} type="date" value={awardDate} onChange={e => setAwardDate(e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Contract type</label>
          <select style={inp} value={contractType} onChange={e => setContractType(e.target.value)}>
            <option value="firm_fixed_price">Firm fixed price</option>
            <option value="cost_reimbursable">Cost-reimbursable</option>
            <option value="time_and_materials">Time & materials</option>
            <option value="iqc">IQC / IDIQ</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
        <DocumentUploader value={igceDocId}
          onChange={setIgceDocId}
          docType="igce" label="Independent cost estimate (IGE) PDF"
          hint="Required for procurements > $350k." />
        <DocumentUploader value={costAnalysisDocId}
          onChange={setCostAnalysisDocId}
          docType="cost_analysis" label="Cost or price analysis"
          hint="Required for any procurement over the simplified threshold." />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
        <DocumentUploader value={coiDocId}
          onChange={setCoiDocId}
          docType="conflict_of_interest" label="Conflict of interest disclosure"
          hint="2 CFR 200.318(c)." />
        <DocumentUploader value={contractDocId}
          onChange={setContractDocId}
          docType="contract" label="Awarded contract"
          hint="Final executed contract PDF." />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onCancel} style={{
          padding: '6px 14px', background: '#fff', color: '#0f172a',
          border: '1px solid var(--border, #e2e8f0)', borderRadius: 4,
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>Cancel</button>
        <button onClick={handleSubmit} disabled={!eventCode || !description || !estimated || isSubmitting}
          style={{
            padding: '6px 14px',
            background: !eventCode || !description || !estimated || isSubmitting ? '#94a3b8' : '#0f172a',
            color: '#fff', border: 'none', borderRadius: 4,
            fontSize: 12, fontWeight: 600,
            cursor: !eventCode || !description || !estimated || isSubmitting ? 'not-allowed' : 'pointer',
          }}>{isSubmitting ? 'Saving…' : 'Save event'}</button>
      </div>
    </div>
  );
}
